import React, { useState } from "react";
import { DownloadSimpleIcon, TrashIcon, CopyIcon } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { customerApi, apiError } from "@/lib/customerApi";
import { isOnline } from "./DashboardHeader";

export default function MyBots({ me, onChange }) {
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);
  const download = async (p) => {
    setErr("");
    try {
      const r = await customerApi.get(`/store/account/download/${p.store_id}`, { responseType: "blob" });
      const name = (r.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || `BotGenomix_${p.store_id}.ex5`;
      const url = URL.createObjectURL(r.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: name });
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(e?.response?.status === 404 ? "That bot's protected file is still being built - try again shortly." : apiError(e));
    }
  };
  const unbind = async (sid, acct) => onChange((await customerApi.delete(`/store/account/accounts/${sid}/${acct}`)).data);
  const regen = async () => {
    if (!window.confirm("Generate a new licence key? Your bots will stop until you paste the new key into each one.")) return;
    onChange((await customerApi.post("/store/account/license/regenerate")).data);
  };
  const copy = () => { navigator.clipboard?.writeText(me.license_key); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  return (
    <section data-testid="my-bots" className="border border-blue-900/40 bg-[#020617]/70 rounded-xl p-6">
      <div className="mono text-[10px] uppercase tracking-widest text-[#FBBF24]">Step 3 · download + set up</div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <span className="text-xs text-[#94A3B8]">Your licence key</span>
        <code data-testid="license-key" className="mono text-sm px-3 py-1.5 rounded bg-[#0B1220] border border-blue-900/50 text-[#FBBF24]">{me.license_key}</code>
        <button onClick={copy} data-testid="license-copy-btn" className="mono text-[10px] uppercase flex items-center gap-1 text-[#38BDF8]"><CopyIcon size={12} />{copied ? "Copied" : "Copy"}</button>
        <button onClick={regen} data-testid="license-regenerate-btn" className="mono text-[10px] uppercase text-[#94A3B8] hover:text-white">Regenerate</button>
      </div>
      {err && <div className="text-xs text-[#F87171] mt-3" data-testid="my-bots-error">{err}</div>}
      {me.picks.length === 0 ? (
        <div className="text-xs text-[#64748B] mt-5" data-testid="my-bots-empty">{me.subscription?.active ? "Pick your bots above, then download them here." : "Choose a plan to unlock downloads."}</div>
      ) : (
        <div className="mt-5 space-y-3">
          {me.picks.map((p) => (
            <div key={p.store_id} data-testid={`my-bot-${p.store_id}`} className="border border-blue-900/40 rounded-lg p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div><div className="text-sm">{p.name || p.store_id}</div><div className="mono text-[10px] text-[#64748B]">Attach to {p.symbol} {p.period} chart</div></div>
                <button onClick={() => download(p)} disabled={!me.subscription?.active} data-testid={`download-${p.store_id}`}
                  className="flex items-center gap-1.5 mono text-[10px] uppercase tracking-widest px-4 py-2 rounded-lg bg-[#38BDF8] text-[#050914] disabled:opacity-40">
                  <DownloadSimpleIcon size={14} /> {p.download_ready ? "Download .ex5" : "Building - try download"}
                </button>
              </div>
              <div className="mt-3 text-xs text-[#94A3B8]">
                MT5 accounts linked ({p.accounts.length}/2 · 1 demo + 1 live):{" "}
                {p.accounts.length === 0 && <span className="text-[#64748B]">none yet - links automatically the first time the bot starts.</span>}
                {p.accounts.map((a) => (
                  <span key={a.account} className="inline-flex items-center gap-1 mr-3">
                    <span className={`w-2 h-2 rounded-full ${isOnline(a.last_seen) ? "bg-[#34D399]" : "bg-[#475569]"}`} title={a.last_seen ? `last licence check ${new Date(a.last_seen).toLocaleString("en-GB")}` : "not seen yet"} data-testid={`account-status-${p.store_id}-${a.account}`} />
                    <span className="mono">{a.account}</span> <span className="text-[#64748B]">({a.demo ? "demo" : "live"}{a.last_seen ? ` · seen ${new Date(a.last_seen).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""})</span>
                    <button onClick={() => unbind(p.store_id, a.account)} data-testid={`unbind-${p.store_id}-${a.account}`} className="text-[#F87171]" title="Unlink"><TrashIcon size={12} /></button>
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <Link to="/account/setup" data-testid="my-bots-guide-link" className="mt-6 flex items-center justify-between gap-3 rounded-xl border-2 border-[#FBBF24]/60 bg-[#FBBF24]/10 px-5 py-4 hover:bg-[#FBBF24]/15 transition-colors">
        <div>
          <div className="text-sm font-semibold text-[#FDE68A]">New to MT5? Follow the 12-step picture guide</div>
          <div className="text-xs text-[#94A3B8] mt-0.5">Pictures, your own licence key filled in, and fixes for every common problem.</div>
        </div>
        <span className="text-[#FBBF24] text-lg">→</span>
      </Link>
    </section>
  );
}
